import type { ComponentType } from 'react';
import { ChevronDownIcon } from './icons';

/**
 * 목업 `.filter-dd` — 아이콘 + 라벨 + **brand 값 태그** + 쉐브론이 든 pill 버튼.
 *
 * 겉모습은 버튼이지만 실제 컨트롤은 위에 투명하게 얹은 native `<select>`다 — 드롭다운
 * UX(키보드·스크린리더·모바일 시트)를 직접 재구현하지 않기 위해서다. 시각층은
 * `aria-hidden`이고 select가 aria-label을 쥔다. 값이 '전체'가 아니면 태그에 선택된
 * 옵션 라벨이 올라온다(시안 `역할 [전체]` 골격).
 *
 * 처음엔 `pages/members/Members/MembersDesktop.tsx` 안에 있었고, 문서관리 툴바가 같은
 * pill을 필요로 하면서 이 자리로 올렸다(2026-08-02). 두 페이지가 같은 목업 컴포넌트를
 * 각자 그리면 태그 색·높이 같은 세부가 갈린다.
 */
export function FilterPill({
  icon: Icon,
  label,
  ariaLabel,
  value,
  options,
  onChange,
  allLabel = '전체',
}: {
  icon: ComponentType<{ className?: string }>;
  label: string;
  ariaLabel: string;
  value: string;
  /** '전체' 옵션은 컴포넌트가 넣는다 — 소비처는 실제 선택지들만 넘긴다. */
  options: { value: string; label: string }[];
  onChange: (value: string) => void;
  /**
   * 빈 값(첫 옵션)의 라벨. 좁히는 필터에서는 '전체'가 맞지만, 정렬처럼 빈 값이 "고르지
   * 않음"을 뜻하는 축에서는 그 말이 거짓이 된다(현장 관리 정렬 pill은 '기본'을 쓴다).
   */
  allLabel?: string;
}) {
  const selected = options.find((option) => option.value === value);

  return (
    <div className="relative inline-flex focus-within:rounded-[10px] focus-within:ring-2 focus-within:ring-brand-500">
      <div
        aria-hidden="true"
        className="flex h-[38px] items-center gap-2 rounded-[10px] border border-line bg-white px-3 text-[14.5px] font-semibold text-ink-700 transition-colors hover:bg-surface-hover"
      >
        <Icon className="h-3.5 w-3.5 flex-none" />
        {label}
        <span className="max-w-[140px] truncate rounded-[5px] bg-brand-50 px-1.5 py-px text-[12.5px] font-bold tracking-[.01em] text-brand-700">
          {selected?.label ?? allLabel}
        </span>
        <ChevronDownIcon className="-ml-0.5 h-[13px] w-[13px] flex-none text-ink-400" />
      </div>
      <select
        aria-label={ariaLabel}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="absolute inset-0 h-full w-full cursor-pointer opacity-0"
      >
        <option value="">{allLabel}</option>
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    </div>
  );
}
